// src/App.jsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { KeyboardControls, Loader } from '@react-three/drei';
import { Experience } from './components/Experience';
import { CharacterSelection } from './components/CharacterSelection';
import './styles/WalletStyles.css';
import { getUserByWallet, registerUser, updateUserCharacter } from './services/userService';
import './styles/Registration.css';
import SocialMenu, { friendEvents } from './components/SocialMenu';
import LeaderboardPage from './components/LeaderboardPage';
import './styles/LoadingTransition.css';
import LandingPage from './components/LandingPage';
import RoadmapPage from './components/RoadmapPage';
import WalletConnection, { WalletConnectionProvider } from './components/WalletConnection';
import StarsBackground from './components/StarsBackground';

function App() {
  const [walletAddress, setWalletAddress] = useState('');
  const [walletConnected, setWalletConnected] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [username, setUsername] = useState('');
  const [user, setUser] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [isPointerLocked, setIsPointerLocked] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  
  const location = useLocation();
  
  // Reset showLandingPage when navigating to root
  useEffect(() => {
    if (location.pathname === '/') {
      setShowLandingPage(true);
    } else {
      setShowLandingPage(false);
    }
  }, [location]);

  const handleWalletDisconnected = () => {
    console.log("Wallet disconnected");
    setWalletConnected(false);
    setWalletAddress('');
    setUser(null);
    setSelectedCharacter(null);
    setIsRegistering(false);
    setError('');
  };  
  
  const handleWalletConnected = (address) => {
    if (address && address.length >= 32) {
      console.log("Wallet connected:", address);
      setWalletAddress(address);
      setWalletConnected(true);
      setError('');
      
      // Immediately check if user exists to avoid flashing character selection
      const checkUserExists = async () => {
        try {
          const userData = await getUserByWallet(address);
          console.log("User found after wallet connection:", userData);
          setUser(userData);
          setSelectedCharacter(userData.character);
          setIsRegistering(false);
        } catch (error) {
          console.log("User not found after wallet connection, will register");
          setIsRegistering(true);
          setUser(null);
        }
      };
      
      checkUserExists();
    } else {
      setError("Invalid wallet address received");
    }
  };

  useEffect(() => {
    if (walletConnected && walletAddress) {
      const checkUserExists = async () => {
        try {
          const userData = await getUserByWallet(walletAddress);
          console.log("User found:", userData);
          setUser(userData);
          setSelectedCharacter(userData.character);
          setIsRegistering(false); // Ensure registration is turned off for existing users
        } catch (error) {
          console.log("User not found, triggering registration");
          setIsRegistering(true);
        }
      };
      
      checkUserExists();
    }
  }, [walletConnected, walletAddress]);

  const handleReturnHome = () => {
    // Navigate back to landing page without disconnecting wallet
    setShowLandingPage(true);
  };

  // Combined handler for character selection and registration
  const handleRegistrationComplete = async (characterId, username) => {
    console.log("Registration complete with:", characterId, username);
    
    if (username) {
      try {
        setError(''); // Clear any previous errors
        const userData = await registerUser(walletAddress, username, characterId);
        setUser(userData);
        setSelectedCharacter(characterId);
        setIsRegistering(false);
      } catch (error) {
        console.error("Registration error:", error);
        // Make sure the error is displayed in the UI
        setError(error.response?.data?.message || error.message || "Registration failed");
        // Keep the user in registration mode
        setIsRegistering(true);
      }
    } else {
      // If no username, it's just character selection
      setSelectedCharacter(characterId);
    }
  };

  const handleCharacterSelected = (characterId) => {
    console.log("Character selected:", characterId);
    setSelectedCharacter(characterId);
    
    if (isRegistering) {
      return;
    }
    
    if (user) {
      const updateCharacter = async () => {
        try {
          const updatedUser = await updateUserCharacter(walletAddress, characterId);
          setUser(updatedUser);
        } catch (error) {
          console.error("Failed to update character:", error);
        }
      };
      
      updateCharacter();
    }
  };

  const handlePointerLockChange = (isLocked) => {
    if (!showLeaderboard) {
      setIsPointerLocked(isLocked);
    }
  };

  const handleToggleLeaderboard = (show) => {
    if (show) {
      // Show leaderboard immediately
      setShowLeaderboard(true);
      
      // Release pointer lock when showing leaderboard
      if (isPointerLocked) {
        // Find pointer lock controls and unlock
        const pointerLockElement = document.pointerLockElement || 
                                  document.mozPointerLockElement || 
                                  document.webkitPointerLockElement;
        if (pointerLockElement) {
          document.exitPointerLock = document.exitPointerLock || 
                                   document.mozExitPointerLock || 
                                   document.webkitExitPointerLock;
          document.exitPointerLock();
        }
      }
    } else {
      // Start transition when returning to 3D world
      setIsTransitioning(true);
      
      // After showing loading transition, hide leaderboard
      setTimeout(() => {
        setShowLeaderboard(false);
        
        // Wait for canvas to load, then hide transition
        setTimeout(() => {
          setIsTransitioning(false);
        }, 1500); // Adjust this timing based on how long your 3D scene takes to render
      }, 500); // Short delay to show the transition screen
    }
  };

  // Handler for friend updates
  const handleFriendUpdate = () => {
    console.log("Friend update triggered from App");
    if (friendEvents && typeof friendEvents.emit === 'function') {
      friendEvents.emit();
    } else {
      console.warn("friendEvents.emit is not available");
    }
  };

  // Handler for Join Waitlist button on landing page
  const handleJoinWaitlist = () => {
    setShowLandingPage(false);
  };

  // Modified to handle routing
  return (
    <WalletConnectionProvider>
      <Routes>
        {/* Roadmap Page Route */}
        <Route path="/roadmap" element={
          <>
            <div className="wallet-header">
              <WalletConnection 
                onWalletConnected={handleWalletConnected} 
                onWalletDisconnected={handleWalletDisconnected}
                connected={walletConnected}
              />
            </div>
            <RoadmapPage />
          </>
        } />

        {/* Main App Route */}
        <Route path="/" element={
          <>
            {showLandingPage ? (
              <>
                <div className="wallet-header">
                  <WalletConnection 
                    onWalletConnected={handleWalletConnected} 
                    onWalletDisconnected={handleWalletDisconnected}
                    connected={walletConnected}
                  />
                </div>
                <LandingPage onJoinWaitlist={handleJoinWaitlist} />
              </>
            ) : (
              <>
                {!walletConnected && (
  <div className="wallet-connection-container">
    <StarsBackground />
    <h1 className="welcome-heading">Welcome to Solcialize</h1>
    <p className="welcome-subheading">Solana's First Ever Social Platform</p>
    <div className="wallet-connect-button">
      <WalletConnection 
        onWalletConnected={handleWalletConnected}
        onWalletDisconnected={handleWalletDisconnected}
        connected={walletConnected}
      />
    </div>
    {error && <p className="error-message">{error}</p>}
  </div>
)}
                
                {walletConnected && isRegistering && (
                  <>
                    <div className="wallet-header">
                      <WalletConnection 
                        onWalletConnected={handleWalletConnected} 
                        onWalletDisconnected={handleWalletDisconnected}
                        connected={walletConnected}
                      />
                    </div>
                    <CharacterSelection 
                      onCharacterSelected={handleRegistrationComplete} 
                      registrationError={error}
                    />
                  </>
                )}
                
                {/* Only show character selection if not registered (for new users), or if changing character */}
                {walletConnected && !isRegistering && !selectedCharacter && !user && (
                  <>
                    <div className="wallet-header">
                      <WalletConnection 
                        onWalletConnected={handleWalletConnected} 
                        onWalletDisconnected={handleWalletDisconnected}
                        connected={walletConnected}
                      />
                    </div>
                    <CharacterSelection onCharacterSelected={handleCharacterSelected} />
                  </>
                )}
                
                {/* Show the 3D world when the user is registered and has a character */}
                {walletConnected && !isRegistering && (user || selectedCharacter) && (
                  <>
                    {/* Loading Transition Overlay */}
                    {isTransitioning && (
                      <div className="loading-transition-overlay">
                        <div className="loading-content">
                          <div className="cyberpunk-loader"></div>
                          <p>Loading solcialize</p>
                        </div>
                      </div>
                    )}
                  
                    {/* Show Leaderboard or 3D World */}
                    {showLeaderboard ? (
                      <>
                        <div className="wallet-header">
                          <WalletConnection 
                            onWalletConnected={handleWalletConnected} 
                            onWalletDisconnected={handleWalletDisconnected}
                            connected={walletConnected}
                          />
                        </div>
                        <LeaderboardPage 
                          walletAddress={walletAddress} 
                          onReturn={() => handleToggleLeaderboard(false)}
                          onFriendUpdate={handleFriendUpdate}
                        />
                      </>
                    ) : (
                      <>
                        <div className="wallet-header">
                          <WalletConnection 
                            onWalletConnected={handleWalletConnected} 
                            onWalletDisconnected={handleWalletDisconnected}
                            connected={walletConnected}
                          />
                        </div>
                        <KeyboardControls
                          map={[
                            { name: 'forward', keys: ['ArrowUp', 'w', 'W'] },
                            { name: 'backward', keys: ['ArrowDown', 's', 'S'] },
                            { name: 'left', keys: ['ArrowLeft', 'a', 'A'] },
                            { name: 'right', keys: ['ArrowRight', 'd', 'D'] },
                            { name: 'sprint', keys: ['Shift'] },
                            // { name: 'jump', keys: ['Space'] },
                            { name: 'rotateLeft', keys: ['q', 'Q'] },
                            { name: 'rotateRight', keys: ['e', 'E'] },
                          ]}
                        >
                          <Canvas
                            id="canvas"
                            shadows
                            camera={{
                              position: [0, 0.8, 3],
                              fov: 75,
                            }}
                          >
                            <Experience 
                              characterType={selectedCharacter}
                              walletAddress={walletAddress}
                              onLockChange={handlePointerLockChange}
                            />
                          </Canvas>
                          <Loader />
                        </KeyboardControls>
                        
                        <SocialMenu 
                          walletAddress={walletAddress} 
                          isPointerLocked={isPointerLocked}
                          onToggleLeaderboard={handleToggleLeaderboard}
                          onReturnHome={handleReturnHome}
                          onDisconnect={handleWalletDisconnected}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        } />

      </Routes>
    </WalletConnectionProvider>
  );
}

export default App;